// Where EZSpot operates, as polygons, plus the coordinate test that gates
// search on them.
//
// VENDORED COPY. The canonical version is `ezspot-mobile/src/lib/serviceArea.ts`
// and the API will grow a third copy when it enforces the same rule server-side.
// Change all of them together until `GET /markets` exists and clients fetch the
// geometry instead of shipping it.
//
// Mobile's copy carries more than this: a second polygon set for ghost-pin
// placement and a city-name check for its host listing wizard. Neither has a
// counterpart on web, so neither is vendored here.

/** An open ring of `[lat, lng]` vertices — the first is not repeated. */
export type LatLngRing = ReadonlyArray<readonly [number, number]>;

// San Francisco's landmass, traced loosely and outside the real shoreline on
// purpose: the west edge runs past the beach into the surf so Ocean Beach and
// Cliff House resolve as in-area, and the south-east edge runs out over the Bay
// so the Hunters Point shipyard does.
const SF_LANDMASS: LatLngRing = [
  [37.708, -122.514], // SW — county line, seaward of the Great Highway
  [37.782, -122.518], // NW — Cliff House / Sutro Baths
  [37.7895, -122.5105], // Lands End / Point Lobos
  [37.79, -122.49], // Baker Beach
  [37.811, -122.478], // Fort Point / Golden Gate Bridge anchor
  [37.81, -122.446], // Crissy Field shoreline
  [37.809, -122.428], // Marina Green
  [37.812, -122.41], // Aquatic Park / Fisherman's Wharf
  [37.81, -122.398], // Pier 39
  [37.797, -122.385], // Ferry Building
  [37.779, -122.383], // South Beach / Oracle Park
  [37.76, -122.378], // Mission Rock / Pier 70
  [37.742, -122.375], // Central Waterfront / Islais Creek
  [37.728, -122.352], // Hunters Point shipyard
  [37.716, -122.372], // Candlestick Point
  [37.708, -122.386], // SE — county line near Bayshore
];

// Treasure Island and Yerba Buena Island — part of the city, off the landmass.
const SF_ISLANDS: LatLngRing = [
  [37.803, -122.382],
  [37.834, -122.382],
  [37.834, -122.358],
  [37.803, -122.352],
];

/** Where EZSpot operates. Expand when launching a new city. */
export const SERVICE_AREA_POLYGONS: ReadonlyArray<LatLngRing> = [
  SF_LANDMASS,
  SF_ISLANDS,
];

/** Display name for the market a coordinate falls in. Currently one. */
export const SERVICE_AREA_NAME = "San Francisco";

function pointInPolygon(
  lat: number,
  lng: number,
  polygon: LatLngRing,
): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    if (!a || !b) continue;
    const [aLat, aLng] = a;
    const [bLat, bLng] = b;
    const crosses =
      aLat > lat !== bLat > lat &&
      lng < ((bLng - aLng) * (lat - aLat)) / (bLat - aLat) + aLng;
    if (crosses) inside = !inside;
  }
  return inside;
}

/**
 * True when a search destination coordinate falls inside an EZSpot market.
 *
 * Coordinates rather than the Mapbox city string: a destination always has one
 * (shared links, events and past bookings included), and the API can only ever
 * enforce the same rule on coordinates. Near a border the polygon is the truth
 * and the city name is just copy.
 */
export function isWithinServiceArea(lat: number, lng: number): boolean {
  return SERVICE_AREA_POLYGONS.some((ring) => pointInPolygon(lat, lng, ring));
}

/**
 * Headline for a blocked destination. Names the city when Mapbox gave us one,
 * except when that city *is* the market: water and bridge-span POIs (Alcatraz,
 * the Golden Gate mid-span) fall outside the polygons while Mapbox still
 * reports them as San Francisco, and "We're not in San Francisco yet" over a
 * map of San Francisco is worse than saying nothing.
 */
export function outOfAreaHeadline(city: string | null | undefined): string {
  // Blank as well as absent: `retrievePlace` reports `city: ""` for a POI
  // Mapbox gives no place context for, and `?city=` in a shared link arrives
  // the same way. Both used to interpolate into "We're not in  yet".
  const name = city?.trim();
  if (!name || name.toLowerCase() === SERVICE_AREA_NAME.toLowerCase()) {
    return "We're not there yet";
  }
  return `We're not in ${name} yet`;
}
